import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  HiOutlineMapPin,
  HiOutlinePhone,
  HiOutlineEnvelope,
  HiOutlineClock,
} from 'react-icons/hi2';
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from 'react-icons/fa';
import { useSettings, useContactForm } from '../hooks/useApi';
import { useLanguage } from '../context/LanguageContext';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

function ContactForm() {
  const { t } = useLanguage();
  const { data: settings } = useSettings();
  const { submitContact, loading, error, success, reset } = useContactForm();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  // Reset success message after 6s
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => reset(), 6000);
      return () => clearTimeout(t);
    }
  }, [success, reset]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t('form.errName');
    if (!form.email.trim()) {
      errs.email = t('form.errEmail');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = t('form.errEmailInvalid');
    }
    if (!form.message.trim()) errs.message = t('form.errMessage');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const ok = await submitContact(form);
    if (ok) setForm(initialForm);
  };

  const s = settings?.settings || settings || {};
  const address = s.address || 'The Connect, 44/246 ซ.วิภาวดีรังสิต แขวงสนามบิน ดอนเมือง กรุงเทพฯ 10210';
  const phone = s.phone || '063-782-4516';
  const email = s.contact_email || 'thitisquall@gmail.com';
  const hours = s.business_hours || 'Mon - Fri: 9:00 AM - 6:00 PM';

  const socialLinks = {
    facebook: s.facebook_url || 'https://www.facebook.com/kbyinteractive',
    instagram: s.instagram_url || '#',
    linkedin: s.linkedin_url || '#',
    youtube: s.youtube_url || 'https://www.youtube.com/@kbyinteractive',
  };

  return (
    <section id="contact" className="section contact">
      <div className="container">
        <motion.div
          className="section__header"
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="section__label">{t('contact.label')}</span>
          <h2 className="section__title">{t('contact.title')}</h2>
          <div className="divider" />
          <p className="section__subtitle">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="contact__grid">
          {/* Left: Contact Info */}
          <motion.div
            className="contact__info"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="contact__info-title">{t('contact.together')}</h3>
            <p className="contact__info-desc">
              {t('contact.togetherDesc')}
            </p>

            <div className="contact__info-item">
              <div className="contact__info-icon"><HiOutlineMapPin /></div>
              <div className="contact__info-text">
                <h4>{t('contact.address')}</h4>
                <p>{address}</p>
              </div>
            </div>

            <div className="contact__info-item">
              <div className="contact__info-icon"><HiOutlinePhone /></div>
              <div className="contact__info-text">
                <h4>{t('contact.phone')}</h4>
                <p>{phone}</p>
              </div>
            </div>

            <div className="contact__info-item">
              <div className="contact__info-icon"><HiOutlineEnvelope /></div>
              <div className="contact__info-text">
                <h4>{t('contact.email')}</h4>
                <p>{email}</p>
              </div>
            </div>

            <div className="contact__info-item">
              <div className="contact__info-icon"><HiOutlineClock /></div>
              <div className="contact__info-text">
                <h4>{t('contact.hours')}</h4>
                <p>{hours}</p>
              </div>
            </div>

            <div className="contact__socials">
              <a href={socialLinks.facebook} className="contact__social-link" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FaFacebookF />
              </a>
              <a href={socialLinks.instagram} className="contact__social-link" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FaInstagram />
              </a>
              <a href={socialLinks.linkedin} className="contact__social-link" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FaLinkedinIn />
              </a>
              <a href={socialLinks.youtube} className="contact__social-link" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <FaYoutube />
              </a>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            className="contact__form-wrapper"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {success && (
              <div className="contact__form-message contact__form-message--success">
                {t('form.success')}
              </div>
            )}

            {error && (
              <div className="contact__form-message contact__form-message--error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="contact__form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-name">{t('form.name')}</label>
                  <input
                    id="contact-name"
                    className="form-input"
                    type="text"
                    name="name"
                    placeholder={t('form.namePh')}
                    value={form.name}
                    onChange={handleChange}
                  />
                  {errors.name && <p className="form-error">{errors.name}</p>}
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-email">{t('form.email')}</label>
                  <input
                    id="contact-email"
                    className="form-input"
                    type="email"
                    name="email"
                    placeholder={t('form.emailPh')}
                    value={form.email}
                    onChange={handleChange}
                  />
                  {errors.email && <p className="form-error">{errors.email}</p>}
                </div>
              </div>

              <div className="contact__form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-phone">{t('form.phone')}</label>
                  <input
                    id="contact-phone"
                    className="form-input"
                    type="tel"
                    name="phone"
                    placeholder={t('form.phonePh')}
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-subject">{t('form.subject')}</label>
                  <input
                    id="contact-subject"
                    className="form-input"
                    type="text"
                    name="subject"
                    placeholder={t('form.subjectPh')}
                    value={form.subject}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="contact-message">{t('form.message')}</label>
                <textarea
                  id="contact-message"
                  className="form-textarea"
                  name="message"
                  placeholder={t('form.messagePh')}
                  rows="5"
                  value={form.message}
                  onChange={handleChange}
                />
                {errors.message && <p className="form-error">{errors.message}</p>}
              </div>

              <button
                type="submit"
                className="btn btn--primary btn--lg"
                disabled={loading}
                style={{ width: '100%', marginTop: 8 }}
              >
                {loading ? t('form.sending') : t('form.send')}
              </button>
            </form>
          </motion.div>
        </div>

        {/* Google Map */}
        <motion.div
          className="contact__map"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{
            marginTop: '3rem',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <iframe
            title="KBY Interactive Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1936.5!2d100.6091013!3d13.9497874!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e28394cfc96f19%3A0xeff70296cd165c5d!2z4Lia4Lij4Li04Lip4Lix4LiXIOC5gOC4hOC4muC4teC4p-C4suC4oiDguK3guLTguJnguYDguJXguK3guKPguYzguYHguK3guITguJfguLXguJ8g4LiI4Liz4LiB4Lix4LiU!5e0!3m2!1sth!2sth!4v1710000000000!5m2!1sth!2sth"
            width="100%"
            height="250"
            style={{ border: 0, display: 'block' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
}

export default ContactForm;
